import React, { FC, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import integrationsService from 'services/api/integrations.service';
import { integrationErrorMessage } from 'Views/Integrations/integration-user-messages';

export interface IEpcisIdentityPreview {
    sgtinItemKey?: string;
    companyPrefix?: string;
    itemReference?: string;
    gtin14?: string;
    idpat?: string;
    sampleUrn?: string;
    conflict?: boolean;
    canApply?: boolean;
    error?: string;
}

interface Props {
    productId?: number | null;
    sgtinItemKey?: string | null;
}

const EpcisProductIdentityPreviewPanel: FC<Props> = ({ productId, sgtinItemKey }) => {
    const [preview, setPreview] = useState<IEpcisIdentityPreview | null>(null);
    const [loading, setLoading] = useState(false);

    const loadPreview = async () => {
        if (!productId || !sgtinItemKey) {
            return;
        }
        setLoading(true);
        try {
            const res: any = await integrationsService.previewEpcisProductIdentity(productId, sgtinItemKey);
            setPreview(res.data.preview);
        } catch (err) {
            setPreview({ error: integrationErrorMessage(err, 'Identity preview failed'), conflict: true });
        } finally {
            setLoading(false);
        }
    };

    if (!productId || !sgtinItemKey) {
        return null;
    }

    return (
        <Box className="mt-3 rounded border border-slate-200 bg-slate-50 p-3">
            <Typography variant="subtitle2" className="mb-1 font-semibold text-slate-800">
                SGTIN product identity (EPCIS)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" className="mb-2">
                Item key {sgtinItemKey} — applied automatically on approve when catalog fields are blank.
            </Typography>
            <Button size="small" variant="outlined" onClick={loadPreview} disabled={loading}>
                Preview GTIN / URN
            </Button>
            {preview ? (
                <Box sx={{ mt: 1 }} component="dl" className="text-sm text-slate-700">
                    {preview.error ? (
                        <Typography color="error">{preview.error}</Typography>
                    ) : (
                        <>
                            <div>
                                <strong>GTIN-14:</strong> {preview.gtin14}
                            </div>
                            <div>
                                <strong>Prefix / item ref:</strong> {preview.companyPrefix} · {preview.itemReference}
                            </div>
                            <div>
                                <strong>idpat:</strong> {preview.idpat}
                            </div>
                            <div>
                                <strong>Sample URN:</strong> {preview.sampleUrn}
                            </div>
                            {preview.conflict ? (
                                <Typography color="error" variant="caption">
                                    Conflicts with existing catalog identity — approve will be blocked.
                                </Typography>
                            ) : null}
                        </>
                    )}
                </Box>
            ) : null}
        </Box>
    );
};

export default EpcisProductIdentityPreviewPanel;
